import { ActionIcon, Menu, Tooltip } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import {
  IconBolt,
  IconBrandFacebookFilled,
  IconLayoutGrid,
  IconReceipt,
  IconTemplate,
} from '@tabler/icons-react';
import { trpc } from '../../../lib/trpc';
import { messengerToBubbleProps, renderMessengerContent } from '../registries/messenger-content';
import { CrmPanel, CrmPanelEmpty } from '../CrmPanel';
import { VoiceNoteButton } from '../composer/VoiceNoteButton';
import type { ChannelCapabilities, ComposerSlotCtx } from '../types';

/** Composer slot: a menu of saved Messenger templates; picking one fires it
 *  via the shell's `sendTemplate` action. */
function MessengerTemplatePicker({ ctx }: { ctx: ComposerSlotCtx }) {
  const templates = trpc.facebook.templates.list.useQuery({});
  const rows = templates.data ?? [];
  return (
    <Menu position="top" withinPortal shadow="sm" width={240}>
      <Menu.Target>
        <Tooltip label="Saved templates">
          <ActionIcon size="lg" variant="subtle" color="gray" disabled={ctx.disabled}>
            <IconTemplate size={18} />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Saved templates</Menu.Label>
        {rows.length === 0 && (
          <Menu.Item disabled>{templates.isPending ? 'Loading…' : 'None saved yet'}</Menu.Item>
        )}
        {rows.map((t) => (
          <Menu.Item
            key={t.uid}
            leftSection={
              t.kind === 'generic' ? (
                <IconLayoutGrid size={14} />
              ) : t.kind === 'receipt' ? (
                <IconReceipt size={14} />
              ) : (
                <IconBolt size={14} />
              )
            }
            onClick={() => ctx.sendTemplate({ kind: t.kind, payload: t.payload })}
          >
            {t.name}
          </Menu.Item>
        ))}
        <Menu.Divider />
        <Menu.Item
          component={Link}
          to="/vendor/facebook/templates"
          leftSection={<IconTemplate size={14} />}
        >
          Manage templates…
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

/** Messenger now mirrors Instagram's composer: media, quick-reply snippets,
 *  emoji, and a saved-template picker. Theme accent stays the WhatsApp blue. */
export const messengerCapabilities: ChannelCapabilities = {
  channel: 'messenger',
  accent: 'blue',
  channelIcon: IconBrandFacebookFilled,
  emptyListLabel: 'No conversations yet.',
  emptyThreadLabel: 'Select a Messenger conversation',
  listTitle: 'Messenger',
  list: { showSearch: false, showSort: false },
  composer: {
    attachments: true,
    quickReplies: true,
    emoji: true,
    placeholder: 'Type a Messenger reply…',
    extraButtons: (ctx) => (
      <>
        <VoiceNoteButton onSend={ctx.sendMedia} disabled={ctx.disabled} />
        <MessengerTemplatePicker ctx={ctx} />
      </>
    ),
  },
  bubble: { canReply: false, canEditDelete: false, reactions: false },
  header: { showProfileRefresh: true, showOpenContactLink: true },
  renderContent: renderMessengerContent,
  toBubbleProps: messengerToBubbleProps,
  rightPanel: ({ contactId }) =>
    contactId != null ? <CrmPanel contactId={contactId} hideWhatsAppOnly /> : <CrmPanelEmpty />,
};
